import React from 'react'
import Store from '../../../../store'
import { Space, Tooltip } from 'antd'
import { FilterFilled } from '@ant-design/icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { sortBy } from 'lodash'
import { faEye, faMessage, faUserDoctor, faFileAlt } from '@fortawesome/free-solid-svg-icons'
import { getTabColor } from '../../../lib/utils'

// Standard action color to use throughout
const ACTION_COLOR = 'rgb(24, 144, 255)'

const SystemUsersTemplates = {
  dataModel: {
    isActive: {
      label: 'Status:',
      key: ['extraData', 'isActive'],
      required: false
    },
    firstName: {
      label: 'Provide a first name:',
      key: 'firstName',
      required: true,
      placeholder: 'User`s first name',
      validationMsg: 'Please provide the user`s first name'
    },
    lastName: {
      label: 'Provide a last name:',
      key: 'lastName',
      required: true,
      placeholder: 'User`s last name',
      validationMsg: 'Please provide the user`s last name'
    },
    email: {
      label: 'Provide an email address:',
      key: 'email',
      type: 'email',
      required: true,
      placeholder: 'User`s email address',
      validationMsg: 'Please provide the user`s email address'
    },
    phoneNumber: {
      label: 'Provide a cellphone number:',
      key: 'phoneNumber',
      type: 'phoneNumber',
      required: true,
      placeholder: 'User`s cellphone number',
      validationMsg: 'Please provide the user`s cellphone number'
    },
    role: {
      label: 'Role:',
      key: ['extraData', 'role', 'type'],
      required: true,
      validationMsg: 'Please specify the user`s role'
    },
    isManager: {
      type: 'switch',
      dependency: null,
      checkedChildren: 'Yes',
      uncheckedChildren: 'No',
      label: 'Manager?',
      key: ['extraData', 'role', 'isManager'],
      required: false
    },
    profession: {
      label: 'Profession:',
      key: ['extraData', 'profession'],
      required: true,
      validationMsg: "Please specify the Medical Professional's Profession"
    },
    professionalType: {
      label: 'Professional Type:',
      key: 'professionalType',
      required: true,
      validationMsg: 'Please specify the Medical Professional`s Professional Type'
    },
    mpNumber: {
      label: 'MP Number:',
      key: ['extraData', 'mpNumber'],
      required: true,
      validationMsg: 'Please specify the MP Number'
    },
    ampathId: {
      label: 'Ampath ID:',
      key: ['extraData', 'ampathId'],
      required: true,
      validationMsg: 'Please specify the Ampath ID'
    },
    qualification: {
      label: 'Qualification:',
      key: ['extraData', 'qualification'],
      required: true,
      placeholder: 'e,g, MHSc, BSc, MBChB, etc.',
      validationMsg: 'Please specify qualification'
    },
    services: {
      label: 'Services:',
      key: ['extraData', 'services'],
      required: false
    },
    clinics: {
      label: 'Linked Clinics:',
      key: ['extraData', 'clinics'],
      required: false
    },
    entities: {
      label: 'Linked Entities:',
      key: ['extraData', 'entities'],
      required: true
    }
  },
  dataTemplate: () => {
    return {
      isNew: true,
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      gender: '',
      race: '',
      extraData: {
        isActive: true,
        role: { type: '', isManager: false },
        clinics: [],
        services: [],
        profession: '',
        mpNumber: '',
        ampathId: ''
      }
    }
  },
  columnTemplate: (
    userFilters,
    type,
    clinics,
    token,
    setTeamChatProps,
    handleViewRecord,
    handleStartConsult,
    isCalendar,
    consultLoading
  ) => {
    return [
      {
        title: <h2>Name</h2>,
        dataIndex: 'firstName',
        filterSearch: true,
        key: 'firstName',
        sorter: (a, b) => {
          return a.firstName.localeCompare(b.firstName)
        }
      },
      {
        title: <h2>Surname</h2>,
        dataIndex: 'lastName',
        key: 'lastName',
        sorter: (a, b) => {
          return a.lastName.localeCompare(b.lastName)
        }
      },
      {
        title: <h2>Profession</h2>,
        dataIndex: ['extraData', 'profession'],
        key: 'extraData.profession',
        width: '10%',
        hidden: type === 'medical' ? false : true,
        render: (professionValue, record) => {
          if (!professionValue) {
            return (
              <p style={{ opacity: 0.5, color: token.colorError }}>
                <em>Not Set</em>
              </p>
            )
          }

          let professionLabel = professionValue.charAt(0).toUpperCase() + professionValue.slice(1)

          // Try to get the label from auth professions if available
          const professions = Store.getState().auth?.professions || []
          const profession = professions.find((p) => p.value === professionValue)
          if (profession) {
            professionLabel = profession.label
          }

          return professionLabel
        },
        filters: sortBy(userFilters.professions || [], ['text']),
        onFilter: (value, record) => {
          return record.extraData?.profession?.indexOf(value) === 0
        },
        filterIcon: (
          <Tooltip title='Filter by Profession'>
            <FilterFilled />
          </Tooltip>
        )
      },
      {
        title: <h2>ID/Passport</h2>,
        dataIndex: 'idNo',
        key: 'idNo',
        width: '15%',
        hidden: type === 'patient' ? false : true,
        render: (idNo) => {
          return (
            idNo || (
              <p style={{ opacity: 0.5, color: token.colorError }}>
                <em>Not Recorded</em>
              </p>
            )
          )
        }
      },
      // {
      //   title: <h2>Date Of Birth</h2>,
      //   hidden: (Store.getState().auth.agiliteUser.extraData.profession === 'nurse' ? true : false) || isCalendar,
      //   render: (record) => {
      //     const date = new Date(record.dateOfBirth)
      //     if (isNaN(date)) {
      //       return (
      //         <p>
      //           <em style={{ color: token.colorError, opacity: 0.5 }}>Invalid Date</em>
      //         </p>
      //       )
      //     } else {
      //       return dayjs(date)?.format('DD-MM-YYYY')
      //     }
      //   }
      // },
      {
        title: <h2>Email</h2>,
        width: '20%',
        hidden: (Store.getState().auth.agiliteUser.extraData.profession === 'nurse' ? true : false) || isCalendar,
        render: (record) => {
          return (
            record.email ||
            record.tmpEmail || (
              <p style={{ opacity: 0.5, color: token.colorError }}>
                <em>Not Recorded</em>
              </p>
            )
          )
        }
      },
      {
        title: <h2>Phone</h2>,
        width: '10%',
        hidden: (Store.getState().auth.agiliteUser.extraData.profession === 'nurse' ? true : false) || isCalendar,
        render: (record) => {
          return (
            record.phoneNumber ||
            record.tmpPhoneNumber || (
              <p style={{ opacity: 0.5, color: token.colorError }}>
                <em>Not Recorded</em>
              </p>
            )
          )
        }
      },
      {
        title: <h2>Main Member</h2>,
        width: '10%',
        hidden: type === 'patient' ? false : true,
        render: (record) => {
          return record?.mainMemberName ? record.mainMemberName : 'Yes'
        }
      },
      {
        title: <h2>Role</h2>,
        dataIndex: ['extraData', 'role', 'type'],
        key: 'extraData.role',
        width: '5%',
        hidden: type === 'system' ? false : true,
        render: (roleValue, record) => {
          let roleLabel = 'Role No Longer Exists'

          Store.getState().auth.roles.map((role) => {
            if (roleValue === role.value) {
              roleLabel = role.label
            }

            return null
          })

          return roleLabel
        },
        filters: sortBy(userFilters.roles, ['text']),
        onFilter: (value, record) => {
          return record.extraData.role.type.indexOf(value) === 0
        },
        filterIcon: (
          <Tooltip title='Filter by Role'>
            <FilterFilled />
          </Tooltip>
        )
      },
      // {
      //   title: <h2>Address</h2>,
      //   dataIndex: 'residentialAddress',
      //   key: 'residentialAddress',
      //   hidden: type === 'patient' || Store.getState().auth.agiliteUser.extraData.profession === 'nurse',
      //   width: '10%'
      // },
      // {
      //   title: <h2>Manager</h2>,
      //   dataIndex: ['extraData', 'role', 'isManager'],
      //   hidden: type === 'patient' ? true : false,
      //   key: ['extraData.isManager'],
      //   width: '5%',
      //   render: (isManager) => {
      //     if (isManager)
      //       return (
      //         <center>
      //           <Tooltip title='This medical professional is a manager'>
      //             <FontAwesomeIcon
      //               icon={faCheckCircle}
      //               style={{ fontSize: 20, color: theme.twitterBootstrap.success }}
      //             />
      //           </Tooltip>
      //         </center>
      //       )
      //     else
      //       return (
      //         <center>
      //           <Tooltip title='This medical professional is not a manager'>
      //             <FontAwesomeIcon
      //               icon={faXmarkCircle}
      //               style={{ fontSize: 20, color: theme.twitterBootstrap.danger }}
      //             />
      //           </Tooltip>
      //         </center>
      //       )
      //   },
      //   filterMultiple: false,
      //   filters: [
      //     {
      //       text: 'Is Manager',
      //       value: true
      //     },
      //     {
      //       text: 'Not Manager',
      //       value: false
      //     }
      //   ],
      //   onFilter: (value, record) => {
      //     return record.extraData.role.isManager === value
      //   },
      //   filterIcon: (
      //     <Tooltip title='Filter by Status'>
      //       <FilterFilled />
      //     </Tooltip>
      //   )
      // },
      // {
      //   title: <h2>Status</h2>,
      //   dataIndex: ['extraData', 'isActive'],
      //   hidden: type === 'patient',
      //   width: '5%',
      //   key: ['extraData', 'isActive'],
      //   render: (status) => {
      //     if (status === true)
      //       return (
      //         <center>
      //           <Tooltip title='Active'>
      //             <FontAwesomeIcon
      //               icon={faCheckCircle}
      //               style={{ fontSize: 20, color: theme.twitterBootstrap.success }}
      //             />
      //           </Tooltip>
      //         </center>
      //       )
      //     else
      //       return (
      //         <center>
      //           <Tooltip title='Deactivated'>
      //             <FontAwesomeIcon
      //               icon={faXmarkCircle}
      //               style={{ fontSize: 20, color: theme.twitterBootstrap.danger }}
      //             />
      //           </Tooltip>
      //         </center>
      //       )
      //   },
      //   filterMultiple: false,
      //   filters: [
      //     {
      //       text: 'Active',
      //       value: true
      //     },
      //     {
      //       text: 'Inactive',
      //       value: false
      //     }
      //   ],
      //   onFilter: (value, record) => {
      //     return record.extraData.isActive === value
      //   },
      //   filterIcon: (
      //     <Tooltip title='Filter by Status'>
      //       <FilterFilled />
      //     </Tooltip>
      //   )
      // },
      // {
      //   title: <h2>Medical Aid</h2>,
      //   key: 'medicalAid',
      //   dataIndex: 'medicalAid',
      //   hidden: type !== 'patient',
      //   render: (medicalAid) => {
      //     return medicalAid.name || 'Private'
      //   }
      // },

      {
        title: <h2>Actions</h2>,
        key: 'actions',
        hidden: Store.getState().auth.agiliteUser.extraData.role.type === 'patient' || isCalendar,
        width: '5%',
        render: (text, record) => {
          return (
            <Space style={{ width: '100%', gap: 12 }}>
              <Tooltip title='Patient Records'>
                <FontAwesomeIcon
                  fontSize={20}
                  style={{ color: '#ccc', transition: 'color 300ms ease' }}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleViewRecord(record)
                  }}
                  icon={faFileAlt}
                  onMouseOver={(e) => {
                    e.currentTarget.style.color = ACTION_COLOR
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.color = '#ccc'
                  }}
                />
              </Tooltip>
              <Tooltip title='Team Chat'>
                <FontAwesomeIcon
                  fontSize={20}
                  onMouseOver={(e) => {
                    e.currentTarget.style.color = ACTION_COLOR
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.color = '#ccc'
                  }}
                  style={{ color: '#ccc', transition: 'color 300ms ease' }}
                  onClick={(e) => {
                    e.stopPropagation()
                    setTeamChatProps({ userRef: record._id, isOpen: true })
                  }}
                  icon={faMessage}
                />
              </Tooltip>
            </Space>
          )
        }
      }
    ]
  }
}

export default SystemUsersTemplates
